import { motion } from "framer-motion";
import { Crown, Layers, Shield } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import ApplyCta from "./ApplyCta";

const pillars = [
  {
    icon: Crown,
    title: "Capital de Marca",
    description: "Para que la percepción del mercado sume valor al negocio en lugar de erosionarlo.",
  },
  {
    icon: Layers,
    title: "Arquitectura de Marca",
    description: "Para ordenar crecimiento, extensiones y unidades sin diluir prestigio.",
  },
  {
    icon: Shield,
    title: "Sistema de Marca",
    description: "Para asegurar coherencia visual, verbal y estratégica, incluso cuando otros ejecutan.",
  },
];

const BrandwayElite = () => {
  return (
    <section id="brandway" className="relative overflow-hidden">
      {/* Part 1: Full-width dark intro */}
      <div
        className="py-24 md:py-36 relative"
        style={{
          background: "linear-gradient(180deg, hsl(215 48% 20%), hsl(217 61% 10%))",
        }}
      >
        {/* Decorative glows */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-3xl opacity-15"
          style={{ backgroundColor: "hsl(216 32% 52%)" }}
        />
        <div
          className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: "hsl(216 35% 65%)" }}
        />

        <div className="container px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-block px-6 py-2 rounded-full border border-[hsl(216_32%_52%/0.4)] bg-[hsl(216_32%_52%/0.1)] text-[hsl(216_35%_75%)] text-sm font-semibold tracking-[0.25em] uppercase mb-10"
            >
              ¿Qué es BrandWay©?
            </motion.span>

            {/* Main headline */}
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] mb-10 text-white tracking-tight">
              Un proceso de{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: "linear-gradient(135deg, #60a5fa 0%, #93c5fd 100%)",
                }}
              >
                120 días
              </span>{" "}
              de transformación
            </h2>

            {/* Divider */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-16 h-px mb-10 origin-left"
              style={{
                background: "linear-gradient(to right, hsl(214 95% 77% / 0.6), transparent)",
              }}
            />

            {/* Two-line description with clear hierarchy */}
            <div className="space-y-6 mb-12 max-w-3xl">
              <p className="text-lg md:text-xl text-white/60 leading-relaxed">
                Para empresas y líderes con amplia trayectoria que atraviesan una nueva etapa de crecimiento, expansión o actualización.
              </p>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Desarrollamos una nueva identidad sólida, coherente y estratégica, alineada al propósito, la visión y la estructura real del negocio.
              </p>
            </div>

            {/* Key statement - standalone */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-2xl md:text-3xl text-white font-medium max-w-3xl leading-snug"
            >
              No se trata de verse mejor.{" "}
              <span
                className="bg-clip-text text-transparent font-semibold"
                style={{
                  backgroundImage: "linear-gradient(135deg, #60a5fa 0%, #93c5fd 100%)",
                }}
              >
                Se trata de valer más
              </span>{" "}
              y sostener ese valor en el tiempo.
            </motion.p>

            <ApplyCta variant="dark" />
          </motion.div>
        </div>
      </div>

      {/* Part 2: Pillars on light background */}
      <div className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium tracking-widest mb-4">
              QUÉ TRABAJAMOS EN PROFUNDIDAD
            </span>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              BrandWay© actúa sobre tres pilares críticos del valor marcario
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + index * 0.1 }}
                className="text-center p-10 rounded-2xl border border-border/50 bg-card hover:border-primary/30 hover-lift group"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                  <pillar.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-4 text-foreground">
                  {pillar.title}
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandwayElite;
